import { useContext, useRef } from 'react'
import { useSetAtom } from 'jotai'
import { useI18n } from '../../../provider/i18n'
import useBasketData from '../../../hooks/useBasketData'
import { GraphQLError } from 'graphql'
import { GetProfilesSubscription } from '../../../generated/urql.client'
import ProfileSelect from '../../../components/NewProfileSelect'
import { MdDelete, MdKeyboardArrowRight } from 'react-icons/md'
import { Blue600, Red800 } from '../../../lib/colors'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { selectSlotModalDataAtom } from '../../../store'
import {
  ConfirmDialog,
  ConfirmDialogType,
} from '../../../components/ConfirmDialog'
import DateButton from '../../../components/DateButton'
import { AuthContext } from '../../../provider/auth'
import { sortProfile } from '../../../lib/sort'

const Consultations = () => {
  const i18n = useI18n()
  const confirmDialogRef = useRef<ConfirmDialogType>(null)
  const setSelectSlotModalData = useSetAtom(selectSlotModalDataAtom)
  const { isLogged } = useContext(AuthContext)
  const { consultations, orderCost, orderCostError, changeItem, removeItem } =
    useBasketData()

  return (
    <div id="consultations-section" className="flex flex-col gap-2">
      <span className="text-lg md:text-xl font-bold">
        {i18n.t('screens.basket.sections.consultations.title')}
      </span>
      <ul className="flex flex-col gap-1">
        {consultations.sort(sortProfile).map((item, i) => {
          const consultationCost = orderCost?.consultations?.find(
            ({ id }) => id === item.consultation.id
          )

          const consultationOutsideSlotError = orderCostError?.graphQLErrors
            .filter(isConsultationOutsideSlotError)
            .find(
              (graphQlError) =>
                graphQlError.extensions.consultationId === item.consultation.id
            )?.extensions

          return (
            <li
              key={`consultation-${item.consultation.id}-${i}`}
              className="border-b border-b-border border-opacity-20 p-4 bg-white flex flex-col md:flex-row gap-4 items-center rounded-lg min-h-16 justify-center"
            >
              <div className="flex gap-4 w-full justify-between">
                <div className="flex flex-col justify-center gap-2">
                  <div>
                    <p className="text-lg font-bold leading-tight">
                      <Link
                        to={`/consultation/${item.consultation.id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <span className="text-black">
                          {item.consultation.name}
                        </span>
                        <MdKeyboardArrowRight
                          size={20}
                          color={Blue600}
                          className="inline"
                        />
                      </Link>
                    </p>
                    {isLogged && (
                      <>
                        <p
                          className="text-blue-700 text-xs"
                          dangerouslySetInnerHTML={{
                            __html: i18n.t(
                              'screens.basket.form.orderConsultations.phoneNumber',
                              {
                                phoneNumber: item.profile?.phoneNumber || '-',
                              }
                            ),
                          }}
                        />
                        {!!consultationOutsideSlotError && (
                          <p className="text-red-700 text-xs hidden md:block">
                            {i18n.t(
                              'screens.basket.form.orderConsultation.outsideSlot'
                            )}
                            <br />
                            {!!consultationOutsideSlotError.prevSlot && (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: i18n.t(
                                    'screens.basket.form.orderConsultation.prevSlot',
                                    {
                                      prevDateTime: moment(
                                        consultationOutsideSlotError.prevSlot
                                          .dateFrom
                                      ).format('YYYY-MM-DD HH:mm'),
                                      prevTime: moment(
                                        consultationOutsideSlotError.prevSlot
                                          .dateTo
                                      ).format('HH:mm'),
                                    }
                                  ),
                                }}
                              />
                            )}
                            {!!consultationOutsideSlotError.nextSlot && (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: i18n.t(
                                    'screens.basket.form.orderConsultation.nextSlot',
                                    {
                                      nextDateTime: moment(
                                        consultationOutsideSlotError.nextSlot
                                          .dateFrom
                                      ).format('YYYY-MM-DD HH:mm'),
                                      nextTime: moment(
                                        consultationOutsideSlotError.nextSlot
                                          .dateTo
                                      ).format('HH:mm'),
                                    }
                                  ),
                                }}
                              />
                            )}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-row items-start md:items-center gap-4">
                  {isLogged && (
                    <>
                      <DateButton
                        value={item.consultationSlot?.dateFrom}
                        className="hidden md:flex"
                        isError={!!consultationOutsideSlotError}
                        onClick={() => {
                          setSelectSlotModalData({
                            isOpen: true,
                            consultationId: item.consultation.id,
                            minDate: moment().format(),
                            slotId: item.consultationSlot?.id,
                            onSelect: (slot) =>
                              changeItem(item, {
                                ...item,
                                consultationSlot: slot,
                              }),
                          })
                        }}
                      />
                      <ProfileSelect
                        className="hidden md:block"
                        onChange={(profile) =>
                          changeItem(item, {
                            ...item,
                            profile,
                          })
                        }
                        value={
                          item.profile as GetProfilesSubscription['client_profile'][number]
                        }
                      />
                    </>
                  )}
                  <div>
                    {consultationCost && (
                      <div className="min-w-10">
                        <p className="text-base font-bold text-blue-700 whitespace-nowrap">
                          {i18n.t('screens.basket.form.orderTrainings.cost', {
                            cost: Number(consultationCost.cost).toFixed(2),
                          })}
                        </p>
                        {consultationCost.costBeforeDiscount && (
                          <p className="text-red-700 line-through text-right font-bold">
                            {i18n.t(
                              'screens.basket.form.orderTrainings.costBeforeDiscount',
                              {
                                cost: Number(
                                  consultationCost.costBeforeDiscount
                                ).toFixed(2),
                              }
                            )}
                          </p>
                        )}
                      </div>
                    )}
                    {!consultationCost && (
                      <p className="text-base font-bold text-blue-700 min-w-10 text-right whitespace-nowrap">
                        {i18n.t('screens.basket.form.orderTrainings.cost', {
                          cost: item.consultation.price,
                        })}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      confirmDialogRef.current?.show({
                        message: i18n.t(
                          'screens.basket.form.orderTrainings.remove.alert.title',
                          {
                            trainingName: item.consultation.name,
                          }
                        ),
                        onSuccess: () => removeItem(item),
                      })
                    }}
                  >
                    <MdDelete size={24} color={Red800} />
                  </button>
                </div>
              </div>
              {isLogged && (
                <>
                  {!!consultationOutsideSlotError && (
                    <p className="text-red-700 text-xs self-start md:hidden">
                      {i18n.t(
                        'screens.basket.form.orderConsultation.outsideSlot'
                      )}
                      <br />
                      {!!consultationOutsideSlotError.prevSlot && (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: i18n.t(
                              'screens.basket.form.orderConsultation.prevSlot',
                              {
                                prevDateTime: moment(
                                  consultationOutsideSlotError.prevSlot.dateFrom
                                ).format('YYYY-MM-DD HH:mm'),
                                prevTime: moment(
                                  consultationOutsideSlotError.prevSlot.dateTo
                                ).format('HH:mm'),
                              }
                            ),
                          }}
                        />
                      )}
                      {!!consultationOutsideSlotError.nextSlot && (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: i18n.t(
                              'screens.basket.form.orderConsultation.nextSlot',
                              {
                                nextDateTime: moment(
                                  consultationOutsideSlotError.nextSlot.dateFrom
                                ).format('YYYY-MM-DD HH:mm'),
                                nextTime: moment(
                                  consultationOutsideSlotError.nextSlot.dateTo
                                ).format('HH:mm'),
                              }
                            ),
                          }}
                        />
                      )}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 sm:w-full md:hidden">
                    <DateButton
                      value={item.consultationSlot?.dateFrom}
                      isError={!!consultationOutsideSlotError}
                      onClick={() => {
                        setSelectSlotModalData({
                          isOpen: true,
                          consultationId: item.consultation.id,
                          minDate: moment().format(),
                          slotId: item.consultationSlot?.id,
                          onSelect: (slot) =>
                            changeItem(item, {
                              ...item,
                              consultationSlot: slot,
                            }),
                        })
                      }}
                    />
                    <ProfileSelect
                      onChange={(profile) =>
                        changeItem(item, {
                          ...item,
                          profile,
                        })
                      }
                      value={
                        item.profile as GetProfilesSubscription['client_profile'][number]
                      }
                    />
                  </div>
                </>
              )}
            </li>
          )
        })}
      </ul>
      <ConfirmDialog ref={confirmDialogRef} />
    </div>
  )
}

const isConsultationOutsideSlotError = (
  error: GraphQLError
): error is GraphQLError & {
  extensions: {
    code: 'consultation-outside-slot'
    consultationId: number
    prevSlot?: {
      dateFrom: string
      dateTo: string
    }
    nextSlot?: {
      dateFrom: string
      dateTo: string
    }
  }
} => {
  return error.extensions?.code === 'consultation-outside-slot'
}

export default Consultations
